import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Container,
  Divider,
  Heading,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  HStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import ReactMarkdown from 'react-markdown'
import HowInterpretationWorksSection from '../components/HowInterpretationWorks'
import { pageTitle } from '../global'
import { useTranslation } from 'react-i18next'
import InterpretAd from '../components/InterpretAd'

const MAX_DAILY_SUBMISSIONS = 3

interface DreamSubmissions {
  date: string
  count: number
}

const useSubmissionLimit = () => {
  const [submissionCount, setSubmissionCount] = useState(0)
  const [isLimited, setIsLimited] = useState(false)

  const checkDailyLimit = () => {
    const today = new Date().toLocaleDateString()
    const storedData = JSON.parse(
      localStorage.getItem('dreamSubmissions') || '{}',
    ) as DreamSubmissions

    if (storedData.date !== today) {
      const newData = {
        date: today,
        count: 0,
      }
      localStorage.setItem('dreamSubmissions', JSON.stringify(newData))
      setSubmissionCount(0)
      setIsLimited(false)
      return true
    }

    setSubmissionCount(storedData.count || 0)
    setIsLimited(storedData.count >= MAX_DAILY_SUBMISSIONS)
    return storedData.count < MAX_DAILY_SUBMISSIONS
  }

  const incrementSubmissionCount = () => {
    const today = new Date().toLocaleDateString()
    const newCount = submissionCount + 1

    localStorage.setItem(
      'dreamSubmissions',
      JSON.stringify({
        date: today,
        count: newCount,
      }),
    )

    setSubmissionCount(newCount)
    setIsLimited(newCount >= MAX_DAILY_SUBMISSIONS)
  }

  useEffect(() => {
    checkDailyLimit()
  }, [])

  return {
    submissionCount,
    isLimited,
    checkDailyLimit,
    incrementSubmissionCount,
    remainingSubmissions: MAX_DAILY_SUBMISSIONS - submissionCount,
  }
}

const useAdBlockDetector = () => {
  const [adBlockDetected, setAdBlockDetected] = useState(false)

  useEffect(() => {
    const detectAdBlock = async () => {
      try {
        await fetch(
          'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js',
          {
            method: 'HEAD',
            mode: 'no-cors',
          },
        )
      } catch (e) {
        setAdBlockDetected(true)
      }
    }

    detectAdBlock()
  }, [])

  return adBlockDetected
}

const DreamInterpretationPage: React.FC = () => {
  const { t } = useTranslation()
  const [dream, setDream] = useState('')
  const [interpretation, setInterpretation] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [adDisplayed, setAdDisplayed] = useState(false)
  const {
    isLimited,
    remainingSubmissions,
    incrementSubmissionCount,
    checkDailyLimit,
  } = useSubmissionLimit()

  const toast = useToast()

  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBgColor = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  const resultBorderColor = useColorModeValue('purple.200', 'purple.700')
  const resultBgColor2 = useColorModeValue('white', 'gray.800')

  const adBlockDetected = useAdBlockDetector()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLimited) {
      toast({
        title: t('common.error'),
        description: t('dreamInterpretation.form.limitReached', {
          count: MAX_DAILY_SUBMISSIONS,
        }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    if (adBlockDetected) {
      toast({
        title: t('dreamInterpretation.adblockWarning.title'),
        description: t('dreamInterpretation.adblockWarning.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)
    setAdDisplayed(true)

    const startTime = Date.now()
    try {
      const response = await fetch('/.netlify/functions/interpretDream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dream: dream.substring(0, 1500) }),
      })

      if (!response.ok) {
        const parsed = await response.json()
        throw new Error(parsed.error || 'Chyba výkladu sna.')
      }

      const data = await response.json()
      const elapsedTime = Date.now() - startTime
      const remainingTime = Math.max(5000 - elapsedTime, 0)

      await new Promise((resolve) => setTimeout(resolve, remainingTime))

      setAdDisplayed(false)
      setInterpretation(data.meaning)
      setInterpretation(data.meaning)
      incrementSubmissionCount()
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 300)
    } catch (error: any) {
      const elapsedTime = Date.now() - startTime
      const remainingTime = Math.max(5000 - elapsedTime, 0)

      const err = error?.toString()?.split(': ')?.[1]
      toast({
        title: t('common.error'),
        description: err || t('dreamInterpretation.form.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })

      await new Promise((resolve) => setTimeout(resolve, remainingTime))
      setAdDisplayed(false)
    } finally {
      setIsLoading(false)
    }
  }

  const title = pageTitle(t('dreamInterpretation.pageTitle'))
  const description = t('dreamInterpretation.description')

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>

      <Modal
        isOpen={adDisplayed}
        onClose={() => setAdDisplayed(false)}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent maxW="90vw">
          <ModalBody pb={6}>
            <InterpretAd />
            <HStack mt={4} justifyContent="center">
              <Spinner /> <Text>{t('dreamInterpretation.form.loading')}</Text>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Box bg={bgColor}>
        <Container maxW="container.xl" py={10}>
          <VStack spacing={8} align="stretch">
            <Heading as="h1" size="2xl" textAlign="center" color={headingColor}>
              {t('dreamInterpretation.title')}
            </Heading>
            <Text fontSize="xl" textAlign="center" color={textColor}>
              {t('dreamInterpretation.description')}
            </Text>

            {adBlockDetected && (
              <Alert status="warning">
                <AlertIcon />
                <AlertTitle mr={2}>
                  {t('dreamInterpretation.adblockWarning.title')}
                </AlertTitle>
                <AlertDescription>
                  {t('dreamInterpretation.adblockWarning.description')}
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </Container>

        <Box bg={resultBgColor2} py={10}>
          <Container maxW="container.xl">
            {!interpretation && (
              <Box as="form" onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <Textarea
                    value={dream}
                    onChange={(e) => setDream(e.target.value)}
                    placeholder={t('dreamInterpretation.form.placeholder')}
                    size="lg"
                    rows={10}
                    isDisabled={adBlockDetected}
                    maxLength={1500}
                  />
                  <Button
                    type="submit"
                    colorScheme="purple"
                    size="lg"
                    isLoading={isLoading}
                    loadingText={t('dreamInterpretation.form.loading')}
                    isDisabled={
                      adBlockDetected || dream.trim() === '' || isLimited
                    }
                  >
                    {t('dreamInterpretation.form.submit')}
                  </Button>
                  {!isLimited && (
                    <Text fontSize="sm" color={textColor}>
                      {t('dreamInterpretation.form.remainingSubmissions', {
                        count: remainingSubmissions,
                      })}
                    </Text>
                  )}
                  {isLimited && (
                    <Text fontSize="sm" color="red.500">
                      {t('dreamInterpretation.form.limitReached', {
                        count: MAX_DAILY_SUBMISSIONS,
                      })}
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

            {interpretation && (
              <VStack spacing={6} align="stretch">
                <Text fontSize="lg" color={textColor} fontStyle="italic">
                  "{dream.slice(0, 200)}..."
                </Text>
                <Divider borderColor={resultBorderColor} />
                <Box
                  bg={cardBgColor}
                  p={6}
                  borderRadius="md"
                  borderWidth="2px"
                  borderColor={resultBorderColor}
                >
                  <Text color={textColor} fontSize="lg">
                    <ReactMarkdown
                      children={interpretation}
                      components={{
                        ol: ({ node, ...props }) => (
                          <ol style={{ marginLeft: '1rem' }} {...props} />
                        ),
                      }}
                    />
                  </Text>
                </Box>
              </VStack>
            )}
          </Container>
        </Box>
        <Container maxW="container.xl" py={10}>
          <HowInterpretationWorksSection />
        </Container>
        <Box bg={resultBgColor2} py={10}>
          <Container maxW="container.xl">
            <Heading as="h2" size="lg" mb={4} color={headingColor}>
              {t('dreamInterpretation.why.title')}
            </Heading>
            <Text color={textColor} mb={4}>
              {t('dreamInterpretation.why.intro')}
            </Text>
            <VStack align="start" spacing={2} color={textColor}>
              <Text>• {t('dreamInterpretation.why.benefits.emotions')}</Text>
              <Text>• {t('dreamInterpretation.why.benefits.concerns')}</Text>
              <Text>• {t('dreamInterpretation.why.benefits.solutions')}</Text>
              <Text>• {t('dreamInterpretation.why.benefits.growth')}</Text>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  )
}

export default DreamInterpretationPage
